"use client";

import { WidgetTemplate } from "@/modules/widget-templates";
import { Activity, BarChart3, Table2, PieChart, TrendingUp, Filter, LayoutGrid, Map, Globe, Link } from "lucide-react";
import { MiniWidgetPreview } from "./MiniWidgetPreview";

const WIDGET_TYPE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
    ticker: Activity,
    chart: BarChart3,
    bar: BarChart3,
    line: TrendingUp,
    pie: PieChart,
    funnel: Filter,
    bubble: Globe,
    table: Table2,
    "top-offers": LayoutGrid,
    "table-map": Map,
    "audience-map": Map,
    "competitor-table": Table2,
};

const WIDGET_TYPE_COLOR_MAP: Record<string, { bg: string; text: string; accent: string; hoverBorder: string }> = {
    ticker: { bg: "bg-emerald-50", text: "text-emerald-600", accent: "border-emerald-200", hoverBorder: "hover:border-emerald-400" },
    chart: { bg: "bg-blue-50", text: "text-blue-600", accent: "border-blue-200", hoverBorder: "hover:border-blue-400" },
    bar: { bg: "bg-blue-50", text: "text-blue-600", accent: "border-blue-200", hoverBorder: "hover:border-blue-400" },
    line: { bg: "bg-indigo-50", text: "text-indigo-600", accent: "border-indigo-200", hoverBorder: "hover:border-indigo-400" },
    pie: { bg: "bg-amber-50", text: "text-amber-600", accent: "border-amber-200", hoverBorder: "hover:border-amber-400" },
    funnel: { bg: "bg-purple-50", text: "text-purple-600", accent: "border-purple-200", hoverBorder: "hover:border-purple-400" },
    bubble: { bg: "bg-teal-50", text: "text-teal-600", accent: "border-teal-200", hoverBorder: "hover:border-teal-400" },
    table: { bg: "bg-slate-50", text: "text-slate-600", accent: "border-slate-200", hoverBorder: "hover:border-slate-400" },
    "top-offers": { bg: "bg-orange-50", text: "text-orange-600", accent: "border-orange-200", hoverBorder: "hover:border-orange-400" },
    "table-map": { bg: "bg-cyan-50", text: "text-cyan-600", accent: "border-cyan-200", hoverBorder: "hover:border-cyan-400" },
    "audience-map": { bg: "bg-cyan-50", text: "text-cyan-600", accent: "border-cyan-200", hoverBorder: "hover:border-cyan-400" },
    "competitor-table": { bg: "bg-rose-50", text: "text-rose-600", accent: "border-rose-200", hoverBorder: "hover:border-rose-400" },
};

const WIDGET_TYPE_LABEL: Record<string, string> = {
    ticker: "Ticker",
    bar: "Bar Chart",
    line: "Line Chart",
    pie: "Pie Chart",
    funnel: "Funnel",
    bubble: "Bubble Chart",
    table: "Table",
    "top-offers": "Offers",
    "table-map": "Map + Table",
    "audience-map": "Map + Table",
    "competitor-table": "Competitor Table",
};

interface TemplateCardProps {
    template: WidgetTemplate;
    isSelected: boolean;
    onClick: () => void;
}

export function TemplateCard({ template, isSelected, onClick }: TemplateCardProps) {
    const widgetType = template.widgetConfig.chartType || template.widgetConfig.widgetType || "chart";
    const IconComponent = WIDGET_TYPE_ICON_MAP[widgetType] || LayoutGrid;
    const colors = WIDGET_TYPE_COLOR_MAP[widgetType] || { bg: "bg-gray-50", text: "text-gray-600", accent: "border-gray-200", hoverBorder: "hover:border-gray-400" };
    const label = WIDGET_TYPE_LABEL[widgetType] || widgetType;

    return (
        <button
            type="button"
            onClick={onClick}
            className={`group w-full rounded-xl border text-left transition-all duration-200 overflow-hidden ${
                isSelected
                    ? "border-[#079a95] ring-2 ring-[#079a95] scale-[1.01]"
                    : `border-[#e0e0e0] ${colors.hoverBorder} hover:-translate-y-0.5 hover:shadow-md`
            }`}
        >
            {/* Preview area */}
            <div className="h-44 border-b border-[#e0e0e0] bg-white overflow-hidden relative">
                <MiniWidgetPreview widgetConfig={template.widgetConfig} />
            </div>

            {/* Info area */}
            <div className="bg-white px-4 py-3">
                <div className="flex items-start gap-2.5">
                    <IconComponent className={`mt-0.5 h-4 w-4 shrink-0 ${isSelected ? "text-[#079a95]" : "text-[#5a717a]"}`} />
                    <div className="min-w-0">
                        <p className={`text-sm font-semibold font-roboto ${isSelected ? "text-[#079a95]" : "text-[#2d3a3f]"}`}>{template.name}</p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-[#5a717a] font-roboto">{template.description}</p>
                    </div>
                </div>
            </div>
        </button>
    );
}
